import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useMemo } from "react";
import type { GraphNode, GraphLink } from "../../../shared/types/tagCloud";

export function useTagCloudData() {
  const data = useQuery(api.tags.getTagCloudData);

  const graphData = useMemo(() => {
    if (!data) return null;

    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];
    const nodeMap = new Map<string, GraphNode>();

    // Calculate min/max counts for scaling
    const counts = data.tags.map(tag => tag.count).filter(count => count > 0);
    const minCount = Math.min(...counts);
    const maxCount = Math.max(...counts);
    const countRange = maxCount - minCount || 1;

    // Create nodes
    for (const tag of data.tags) {
      if (tag.count > 0) { // Only include tags that are actually used
        const normalizedSize = (tag.count - minCount) / countRange;
        const size = 10 + (normalizedSize * 40); // Size between 10 and 50
        
        // Color based on usage frequency (blue to red gradient)
        const hue = 240 - (normalizedSize * 120); // Blue (240) to red (120)
        const color = hslToHex(hue, 70, 50);

        const node: GraphNode = {
          id: tag.id,
          data: {
            name: tag.name,
            count: tag.count,
            size,
            color: Number.parseInt(color.replace('#', ''), 16)
          }
        };

        nodes.push(node);
        nodeMap.set(tag.id, node);
      }
    }

    // Create links based on co-occurrences
    for (const tag of data.tags) {
      if (tag.count > 0 && nodeMap.has(tag.id)) {
        for (const coOcc of tag.coOccurrences) {
          if (nodeMap.has(coOcc.tagId) && coOcc.count > 0) {
            // Only create link if both nodes exist and there's a meaningful connection
            const weight = coOcc.count;
            const maxCoOccurrence = Math.max(...data.tags.flatMap(t => t.coOccurrences.map(c => c.count)));
            const strength = weight / maxCoOccurrence;

            // Avoid duplicate links by only creating links where fromId < toId
            if (tag.id < coOcc.tagId) {
              links.push({
                fromId: tag.id,
                toId: coOcc.tagId,
                data: {
                  weight,
                  strength
                }
              });
            }
          }
        }
      }
    }

    return {
      nodes,
      links,
      totalReferences: data.totalReferences
    };
  }, [data]);

  return {
    data: graphData,
    isLoading: data === undefined,
    error: null
  };
}

// Helper function to convert HSL to hex color
function hslToHex(h: number, s: number, l: number): string {
  const lightness = l / 100;
  const a = s * Math.min(lightness, 1 - lightness) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = lightness - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
} 